import { useEffect, useState, useMemo } from "react";
import { getPortfolio, createPortfolioItem, updatePortfolioItem, deletePortfolioItem, uploadSiteImage } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X, Camera, Loader2, ImageIcon, Eye, EyeOff, Search, ChevronLeft, ChevronRight, ChevronDown, Save } from "lucide-react";
import { logger } from "../../lib/logger";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { CATS, DEFAULT_EMPTY_PORTFOLIO_ITEM, EMPTY_PORTFOLIO, EMPTY_PORTFOLIO_CADAST, TOAST_IMAGE_UPLOADED, TOAST_IMAGE_ERROR, TOAST_NO_PERMISSION, TOAST_REQUIRED, TOAST_SAVED, TOAST_SAVE_ERROR, TOAST_REMOVED, TOAST_REMOVE_ERROR, PLH_PROJECT_NAME, PLH_PROJECT_DESC, PLH_SEARCH_PROJECT, TIP_EDIT, TIP_HIDE, TIP_PUBLISH } from "../../lib/constants";
const empty = DEFAULT_EMPTY_PORTFOLIO_ITEM;
const PAGE_SIZES = [6, 12, 24, 48];

export default function AdminPortfolio({ readOnly = false, activeLang }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [confirmPub, setConfirmPub] = useState(null);

  async function load() { const data = await getPortfolio(activeLang); setItems(data || []); }
  useEffect(() => { load(); }, [activeLang]);

  useEffect(() => { setPage(1); }, [search, catFilter, pageSize]);

  const categories = useMemo(() => [...new Set(items.map(i => i.category).filter(Boolean))], [items]);

  const filtered = useMemo(() => {
    let f = items;
    if (catFilter) f = f.filter(i => i.category === catFilter);
    if (search) { const q = search.toLowerCase(); f = f.filter(i => i.title.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q)); }
    return f;
  }, [items, catFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const publishedCount = items.filter(i => i.published === 1 || i.published === true).length;
  const hiddenCount = items.filter(i => i.published === 0 || i.published === false).length;

  function openNew() { setModal("new"); setForm(empty); }
  function openEdit(p) { setModal(p.uid); setForm({ title: p.title, category: p.category, description: p.description || "", image_url: p.image_url, featured: !!p.featured, published: p.published === 1 || p.published === true }); }
  function closeModal() { setModal(null); setForm(empty); }

  useEffect(() => {
    if (!modal) return;
    function onKey(e) {
      if (e.key === "Escape") closeModal();
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal, form]);

  async function handleImageUpload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const url = await uploadSiteImage(file); setForm(prev => ({ ...prev, image_url: url })); toast.success(TOAST_IMAGE_UPLOADED); }
    catch (err) { logger.error(err); toast.error(TOAST_IMAGE_ERROR); }
    finally { setUploading(false); }
  }

  async function save() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!form.title.trim()) { toast.warning(TOAST_REQUIRED); return; }
    setSaving(true);
    const payload = { ...form, featured: form.featured ? 1 : 0, published: form.published ? 1 : 0 };
    try {
      if (modal === "new") await createPortfolioItem(payload, activeLang);
      else await updatePortfolioItem(modal, payload, activeLang);
      toast.success(TOAST_SAVED); closeModal(); load();
    } catch (err) { logger.error(err); toast.error(TOAST_SAVE_ERROR); }
    finally { setSaving(false); }
  }

  async function togglePublished(p) {
    if (readOnly) return;
    const newVal = p.published === 1 || p.published === true ? 0 : 1;
    try {
      await updatePortfolioItem(p.uid, { published: newVal }, activeLang);
      setItems(prev => prev.map(i => i.uid === p.uid ? { ...i, published: newVal } : i));
    } catch (err) { toast.error(TOAST_SAVE_ERROR); }
  }

  async function remove() {
    if (readOnly || !itemToDelete) return;
    try { await deletePortfolioItem(itemToDelete); toast.success(TOAST_REMOVED); load(); }
    catch (err) { logger.error(err); toast.error(TOAST_REMOVE_ERROR); }
    finally { setItemToDelete(null); }
  }

  return (
    <div data-id="admin-portfolio" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0">
            <ImageIcon size={16} />
          </div>
          <div>
            <p className="text-sm font-medium text-softblack">Portfólio</p>
            <p className="text-[10px] text-warmgray">{items.length} projetos · {publishedCount} publicados · {hiddenCount} ocultos</p>
          </div>
        </div>
        {!readOnly && (
          <button onClick={openNew} className="btn-primary !py-2 !px-4 !text-[10px] flex items-center gap-2 shrink-0 self-start">
            <Plus size={14} /> Novo Projeto
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={PLH_SEARCH_PROJECT} className="w-full pl-9 pr-3 py-2 text-xs border border-fendi/30 rounded-lg bg-white focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
        </div>
        <div className="relative">
          <button onClick={() => setFilterOpen(!filterOpen)} className="w-full sm:w-auto px-4 py-2 text-xs border border-fendi/30 rounded-lg bg-white flex items-center gap-2 hover:border-fendi/60 transition-colors">
            {catFilter || "Todas as categorias"} <ChevronDown size={12} className={`transition-transform ${filterOpen ? "rotate-180" : ""}`} />
          </button>
          {filterOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-fendi/30 rounded-lg shadow-lg py-1 min-w-[160px] z-20" onMouseLeave={() => setFilterOpen(false)}>
              <button onClick={() => { setCatFilter(""); setFilterOpen(false); }} className={`block w-full text-left px-4 py-2 text-xs transition-colors ${!catFilter ? "text-champagne bg-champagne/5 font-medium" : "text-warmgray hover:text-softblack hover:bg-fendi/10"}`}>
                Todas as categorias
              </button>
              {categories.map(c => (
                <button key={c} onClick={() => { setCatFilter(c); setFilterOpen(false); }} className={`block w-full text-left px-4 py-2 text-xs transition-colors ${catFilter === c ? "text-champagne bg-champagne/5 font-medium" : "text-warmgray hover:text-softblack hover:bg-fendi/10"}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {paged.length === 0 ? (
        <div className="bg-white border border-fendi/40 rounded-xl p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-fendi/10 flex items-center justify-center mb-4">
            <ImageIcon size={20} className="text-fendi/40" />
          </div>
          <p className="text-sm text-warmgray">{catFilter || search ? EMPTY_PORTFOLIO : EMPTY_PORTFOLIO_CADAST}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {paged.map((p) => (
            <div key={p.uid} className="bg-white border border-fendi/30 rounded-xl overflow-hidden hover:shadow-md transition-all duration-300 group">
              <div className="relative overflow-hidden aspect-[4/3]">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                ) : (
                  <div className="w-full h-full bg-fendi/5 flex items-center justify-center"><ImageIcon size={36} className="text-fendi/30" /></div>
                )}
                <div className="absolute top-2 left-2 flex gap-1.5">
                  {!!p.featured && <span className="bg-softblack text-white text-[8px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">Destaque</span>}
                </div>
                <button onClick={() => setConfirmPub(p)}
                  className={`absolute top-2 right-2 text-[8px] font-bold px-2 py-0.5 rounded uppercase inline-flex items-center gap-1 transition-all ${p.published === 1 || p.published === true ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-warmgray/70 text-white hover:bg-warmgray/90'}`}
                  title={p.published === 1 || p.published === true ? TIP_HIDE : TIP_PUBLISH}>
                  {p.published === 1 || p.published === true ? <Eye size={9} /> : <EyeOff size={9} />} {p.published === 1 || p.published === true ? "Publicado" : "Oculto"}
                </button>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <p className="text-sm font-medium text-softblack leading-snug line-clamp-1">{p.title}</p>
                  <span className="text-[8px] text-warmgray whitespace-nowrap bg-fendi/10 px-2 py-0.5 rounded uppercase tracking-wider shrink-0">{p.category}</span>
                </div>
                {p.description && <p className="text-xs text-warmgray leading-relaxed line-clamp-2 mb-3">{p.description}</p>}
                {!readOnly && (
                  <div className="flex items-center gap-2 pt-3 border-t border-fendi/10">
                    <button onClick={() => openEdit(p)} className="flex-1 text-[10px] text-warmgray hover:text-softblack py-1.5 rounded-md hover:bg-fendi/5 transition-colors flex items-center justify-center gap-1">
                      <Pencil size={11} /> Editar
                    </button>
                    <button onClick={() => setItemToDelete(p.uid)} className="flex-1 text-[10px] text-warmgray hover:text-red-600 py-1.5 rounded-md hover:bg-red-50 transition-colors flex items-center justify-center gap-1">
                      <Trash2 size={11} /> Eliminar
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length > pageSize && (
        <div className="bg-white border border-fendi/40 rounded-xl p-3 md:p-4 flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
          <div className="flex items-center gap-3">
            <p className="text-xs md:text-sm text-warmgray">
              {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} de {filtered.length}
            </p>
            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
              className="text-xs border border-fendi/30 rounded-lg px-2 py-1.5 bg-white focus:border-champagne outline-none">
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / pág</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1 md:gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
              className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center border border-fendi/30 rounded-lg hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let n;
              if (totalPages <= 5) n = i + 1;
              else if (safePage <= 3) n = i + 1;
              else if (safePage >= totalPages - 2) n = totalPages - 4 + i;
              else n = safePage - 2 + i;
              return (
                <button key={n} onClick={() => setPage(n)}
                  className={`w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-lg text-[11px] md:text-sm transition-colors ${safePage === n ? "bg-softblack text-white" : "border border-fendi/30 hover:bg-fendi/20"}`}>
                  {n}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
              className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center border border-fendi/30 rounded-lg hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl mx-4 w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne">
                  {modal === "new" ? <Plus size={15} /> : <Pencil size={15} />}
                </div>
                <p className="text-sm font-medium text-softblack">{modal === "new" ? "Novo Projeto" : "Editar Projeto"}</p>
              </div>
              <button onClick={closeModal} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Imagem</span>
                <div className="relative group w-full aspect-[4/3] bg-fendi/10 overflow-hidden border border-fendi/30 rounded-lg mt-1.5">
                  {form.image_url ? (
                    <><img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                      <div className="absolute top-2 right-2 z-10"><button onClick={() => setForm(p => ({ ...p, image_url: "" }))} className="bg-white/90 p-1.5 text-red-600 hover:bg-white rounded-lg shadow-sm"><Trash2 size={14} /></button></div></>
                  ) : <div className="w-full h-full flex items-center justify-center text-warmgray"><ImageIcon size={24} strokeWidth={1} /></div>}
                  <label className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all cursor-pointer flex items-center justify-center opacity-0 group-hover:opacity-100 text-white rounded-lg">
                    {uploading ? <Loader2 className="animate-spin" /> : <Camera size={20} />}
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                  </label>
                </div>
              </div>
              <div className="md:col-span-2 space-y-4">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Título <span className="text-red-400">*</span></span>
                  <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={PLH_PROJECT_NAME} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Categoria</span>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all">
                    {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Descrição</span>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder={PLH_PROJECT_DESC} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all resize-none" />
                </div>
                <div className="flex gap-4 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-fendi/20 rounded-lg hover:bg-fendi/5 transition-colors">
                    <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} className="accent-softblack" />
                    <span className="text-[10px] font-medium text-softblack">Destaque</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-fendi/20 rounded-lg hover:bg-fendi/5 transition-colors">
                    <input type="checkbox" checked={form.published ?? true} onChange={e => setForm({ ...form, published: e.target.checked })} className="accent-softblack" />
                    <span className="text-[10px] font-medium text-softblack">Publicado</span>
                  </label>
                </div>
              </div>
            </div>
            {!readOnly && (
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-fendi/10">
                <button onClick={closeModal} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
                <button onClick={save} disabled={saving} className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5">
                  <Save size={12} /> {saving ? "A guardar..." : "Guardar"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi/40 rounded-xl max-w-md">
          <AlertDialogHeader><AlertDialogTitle className="text-lg font-medium text-softblack">Eliminar Projeto?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-warmgray leading-relaxed">Esta ação removerá permanentemente o projeto do portfólio.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter className="flex gap-3 mt-6">
            <AlertDialogCancel className="btn-ghost !border-fendi/30 !py-2 !px-6 text-xs">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-700 !py-2 !px-6 text-xs rounded-lg">Confirmar Eliminação</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmPub} onOpenChange={() => setConfirmPub(null)}>
        <AlertDialogContent className="bg-white border border-fendi/40 rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-medium text-softblack">
              {confirmPub?.published === 1 || confirmPub?.published === true ? "Ocultar Projeto?" : "Publicar Projeto?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-warmgray leading-relaxed">
              Tem a certeza que deseja {confirmPub?.published === 1 || confirmPub?.published === true ? "ocultar" : "publicar"} <strong>{confirmPub?.title}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-3 mt-6">
            <AlertDialogCancel className="btn-ghost !border-fendi/30 !py-2 !px-6 text-xs">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { togglePublished(confirmPub); setConfirmPub(null); }}
              className={`!py-2 !px-6 text-xs rounded-lg ${confirmPub?.published === 1 || confirmPub?.published === true ? "bg-orange-600 text-white hover:bg-orange-700" : "bg-green-600 text-white hover:bg-green-700"}`}>
              {confirmPub?.published === 1 || confirmPub?.published === true ? "Ocultar" : "Publicar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
